import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { resources } from '@/resources';
import { useResourceCount } from '@/lib/use-resource-count';
import { Link } from 'react-router';
import {
  ArrowRight,
  Bot,
  ChartColumn,
  CircleCheck,
  Clock,
  Database,
  FileText,
  Fingerprint,
  Globe,
  KeyRound,
  Layers,
  PenLine,
  ScrollText,
  Server,
  Settings,
  ShieldCheck,
  SlidersHorizontal,
  Users,
  type LucideIcon,
} from 'lucide-react';

// Keyed by resource name. `dashboard.test.ts` pins that every resource in
// `resources.ts` has an entry in BOTH maps — a card without them renders as
// a bare title (how the Usage card shipped in M33).
export const SUMMARIES: Record<string, string> = {
  users: 'Manage admin and member identities.',
  'api-keys': 'Mint, list, and revoke hub API keys.',
  permissions: 'Edit Zanzibar tuples (object → relation → subject).',
  'identity-providers': 'Configure login SSO and the Drive connector OAuth app.',
  'web-research': 'Configure the web search, extract, and browse providers.',
  models: 'Register named LLM models the hub can use.',
  assignments: 'Bind each hub purpose to a registered model.',
  'db-connectors': 'Connect external databases and curate their semantic layers.',
  'mcp-servers': 'Register MCP servers, choose how users connect, approve their tools.',
  documents: 'Browse and search ingested documents across tracks.',
  tracks: 'Configure registered tracks and their API URLs.',
  prompts: 'Curate system prompts and versions.',
  approvals: 'Review and resolve pending agent approvals.',
  schedules: 'Inspect scheduled actions and their runs.',
  audit: 'Every recorded event — who did what, when, on which record, old and new value.',
  usage: 'Recorded activity from the audit log — who and what the hub is used for.',
  settings: 'Hub-wide configuration and appearance.',
};

export const ICONS: Record<string, LucideIcon> = {
  users: Users,
  'api-keys': KeyRound,
  permissions: ShieldCheck,
  'identity-providers': Fingerprint,
  'web-research': Globe,
  models: Bot,
  assignments: SlidersHorizontal,
  'db-connectors': Database,
  'mcp-servers': Server,
  documents: FileText,
  tracks: Layers,
  prompts: PenLine,
  approvals: CircleCheck,
  schedules: Clock,
  audit: ScrollText,
  usage: ChartColumn,
  settings: Settings,
};

export function Dashboard() {
  // Fixed hook set — live totals for the whole-collection resources.
  const counts: Record<string, number | undefined> = {
    users: useResourceCount('users'),
    'api-keys': useResourceCount('api-keys'),
    tracks: useResourceCount('tracks'),
    prompts: useResourceCount('prompts'),
    permissions: useResourceCount('permissions'),
    models: useResourceCount('models'),
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Dashboard</h1>
        <p className="text-sm text-muted-foreground">Pick a section to manage.</p>
      </div>
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
        {resources.map((r) => {
          const Icon = ICONS[r.name];
          return (
            <Link key={r.name} to={r.list as string} className="group">
              <Card className="relative h-full transition-shadow group-hover:shadow-md">
                <CardHeader>
                  <div className="flex items-center gap-3">
                    {Icon && (
                      <span className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-accent text-accent-foreground">
                        <Icon className="size-4.5" aria-hidden="true" />
                      </span>
                    )}
                    <CardTitle className="text-base">{r.meta?.label ?? r.name}</CardTitle>
                    {counts[r.name] !== undefined && (
                      <span className="ml-auto text-sm font-semibold tabular-nums text-muted-foreground">
                        {counts[r.name]}
                      </span>
                    )}
                  </div>
                  <CardDescription className="pt-1">{SUMMARIES[r.name] ?? ''}</CardDescription>
                </CardHeader>
                <CardContent>
                  <ArrowRight
                    className="absolute bottom-4 right-4 size-4 text-primary opacity-0 transition-opacity group-hover:opacity-100 motion-reduce:transition-none"
                    aria-hidden="true"
                  />
                </CardContent>
              </Card>
            </Link>
          );
        })}
      </div>
    </div>
  );
}
